import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Pitch } from "../lib/utils";
import { formatDate, formatRelativeTime, getStatusColor, getPitchTypeColor, getPitchTypeLabel, getMatchScoreColor, getMatchScoreBg, truncate } from "../lib/utils";
import { Card, Badge, Button, LoadingSpinner, EmptyState, Modal, Textarea, Label } from "../components/ui";

export function Pitches() {
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("draft");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPitch, setSelectedPitch] = useState<Pitch | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [editingPitch, setEditingPitch] = useState<Pitch | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");

  useEffect(() => {
    loadPitches();
  }, [status, page]);

  const loadPitches = async () => {
    try {
      setLoading(true);
      const data = await api.pitches.list({ status, limit: 20, offset: page * 20 });
      if (page === 0) {
        setPitches(data.pitches);
      } else {
        setPitches(prev => [...prev, ...data.pitches]);
      }
      setHasMore(data.pitches.length === 20);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load pitches");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => setPage(p => p + 1);

  const handleViewDetail = (pitch: Pitch) => {
    setSelectedPitch(pitch);
    setShowDetail(true);
  };

  const handleEdit = (pitch: Pitch) => {
    setEditingPitch(pitch);
    setEditSubject(pitch.subject);
    setEditBody(pitch.body);
    setShowDetail(false);
  };

  const handleSaveEdit = async () => {
    if (!editingPitch) return;
    try {
      // In a real app, you'd have an update endpoint
      // For now, we'll just close the edit modal
      setEditingPitch(null);
      alert("Edit saved (would call API in production)");
    } catch (err) {
      alert("Failed to save: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleApprove = async (pitchId: string) => {
    if (!confirm("Send this pitch? This will email the recipient.")) return;
    try {
      await api.pitches.approve(pitchId);
      await loadPitches();
    } catch (err) {
      alert("Failed to approve: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleReject = async (pitchId: string) => {
    const feedback = prompt("Optional feedback for rejection:");
    try {
      await api.pitches.reject(pitchId, feedback || undefined);
      await loadPitches();
    } catch (err) {
      alert("Failed to reject: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "approved", label: "Approved" },
    { value: "sent", label: "Sent" },
    { value: "rejected", label: "Rejected" },
    { value: "bounced", label: "Bounced" },
    { value: "replied", label: "Replied" }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pitches</h1>
          <p className="text-slate-500 mt-1">Review and manage generated outreach pitches</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(0); }}
            className="input w-auto"
          >
            {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50 text-red-700">
          {error}
        </div>
      )}

      {loading && pitches.length === 0 ? (
        <LoadingSpinner size="lg" />
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Match</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Recipient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pitches.map((pitch) => (
                    <tr key={pitch.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900 truncate max-w-xs">{pitch.subject}</p>
                          <p className="text-sm text-slate-500 truncate max-w-xs">{truncate(pitch.body, 80)}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={getPitchTypeColor(pitch.pitch_type)}>
                          {getPitchTypeLabel(pitch.pitch_type)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMatchScoreBg(pitch.match_score)} ${getMatchScoreColor(pitch.match_score)}`}>
                          {(pitch.match_score * 100).toFixed(0)}%
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={getStatusColor(pitch.status)}>{pitch.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {pitch.contact_email || pitch.contact_name || "Not set"}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatRelativeTime(pitch.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {status === "draft" && (
                            <>
                              <Button size="sm" variant="ghost" icon="edit" onClick={() => handleEdit(pitch)}>
                                Edit
                              </Button>
                              <Button size="sm" variant="primary" icon="send" onClick={() => handleApprove(pitch.id)}>
                                Send
                              </Button>
                              <Button size="sm" variant="danger" icon="trash" onClick={() => handleReject(pitch.id)}>
                                Reject
                              </Button>
                            </>
                          )}
                          {status !== "draft" && (
                            <Button size="sm" variant="ghost" icon="eye" onClick={() => handleViewDetail(pitch)}>
                              View
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {hasMore && (
              <div className="p-4 border-t border-slate-200">
                <Button onClick={loadMore} className="w-full" variant="secondary">
                  Load More
                </Button>
              </div>
            )}
            
            {pitches.length === 0 && !loading && (
              <EmptyState 
                icon="FileText" 
                title="No pitches found" 
                description={`No ${status} pitches yet.`} 
              />
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title="Pitch Details" size="lg">
        {selectedPitch && (
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <p className="text-slate-900">{selectedPitch.subject}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Badge className={getPitchTypeColor(selectedPitch.pitch_type)}>
                  {getPitchTypeLabel(selectedPitch.pitch_type)}
                </Badge>
              </div>
              <div>
                <Label>Match Score</Label>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMatchScoreBg(selectedPitch.match_score)} ${getMatchScoreColor(selectedPitch.match_score)}`}>
                  {(selectedPitch.match_score * 100).toFixed(0)}%
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Badge className={getStatusColor(selectedPitch.status)}>{selectedPitch.status}</Badge>
              </div>
              <div>
                <Label>Recipient</Label>
                <p className="text-slate-900">{selectedPitch.contact_email || selectedPitch.contact_name || "Not set"}</p>
              </div>
            </div>
            <div>
              <Label>Matched Skills</Label>
              <div className="flex flex-wrap gap-2">
                {selectedPitch.matched_skills.map((skill: string) => (
                  <Badge key={skill} className="bg-slate-100 text-slate-700">{skill}</Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>Body</Label>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 whitespace-pre-wrap text-sm text-slate-700 max-h-96 overflow-y-auto">
                {selectedPitch.body}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Button variant="secondary" onClick={() => setShowDetail(false)}>Close</Button>
              {status === "draft" && (
                <>
                  <Button variant="ghost" icon="edit" onClick={() => { handleEdit(selectedPitch!); setShowDetail(false); }}>Edit</Button>
                  <Button variant="danger" icon="trash" onClick={() => { handleReject(selectedPitch!.id); setShowDetail(false); }}>Reject</Button>
                  <Button variant="primary" icon="send" onClick={() => { handleApprove(selectedPitch!.id); setShowDetail(false); }}>Send</Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editingPitch} onClose={() => setEditingPitch(null)} title="Edit Pitch" size="xl">
        {editingPitch && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-subject">Subject</Label>
              <input
                id="edit-subject"
                type="text"
                value={editSubject}
                onChange={e => setEditSubject(e.target.value)}
                className="input"
                maxLength={200}
              />
            </div>
            <div>
              <Label htmlFor="edit-body">Body</Label>
              <Textarea
                id="edit-body"
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                rows={20}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Button variant="secondary" onClick={() => setEditingPitch(null)}>Cancel</Button>
              <Button variant="primary" icon="check" onClick={handleSaveEdit}>Save Changes</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}